import { useApiUrl, useCustomMutation, useNotification } from "@refinedev/core";
import { Form, Modal, Select } from "antd";
import React from "react";
import { INVALID_EMAIL_MESSAGE, isValidEmail } from "../lib/imo-parsing";
import { Period, periodQueryParams } from "../lib/period";
import { ExportScope } from "./ExportButtons";

interface EmailReportModalProps {
  open: boolean;
  scope: ExportScope;
  period: Period;
  onClose: () => void;
}

interface EmailReportFormValues {
  recipients: string[];
}

export const EmailReportModal: React.FC<EmailReportModalProps> = ({
  open,
  scope,
  period,
  onClose,
}) => {
  const apiUrl = useApiUrl();
  const { open: notify } = useNotification();
  const [form] = Form.useForm<EmailReportFormValues>();
  const {
    mutate,
    mutation: { isPending },
  } = useCustomMutation();

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(({ recipients }) => {
        mutate(
          {
            url: `${apiUrl}/flat-fee-analytics/export/email`,
            method: "post",
            values: { ...scope, ...periodQueryParams(period), recipients },
          },
          {
            onError: (error: any) => {
              const message = error?.response?.data?.message;
              notify?.({
                type: "error",
                message: "Email Failed",
                description:
                  (Array.isArray(message) ? message.join(", ") : message) ||
                  error?.message ||
                  "An error occurred while emailing the report.",
              });
            },
            onSuccess: (data) => {
              const rowCount = (data?.data as any)?.rowCount;
              notify?.({
                type: "success",
                message: "Report Sent",
                description:
                  rowCount !== undefined
                    ? `Report with ${rowCount} row(s) emailed to ${recipients.length} recipient(s).`
                    : `Report emailed to ${recipients.length} recipient(s).`,
              });
              form.resetFields();
              onClose();
            },
          }
        );
      })
      .catch(() => undefined);
  };

  return (
    <Modal
      open={open}
      title="Email report"
      okText="Send"
      confirmLoading={isPending}
      cancelButtonProps={{ disabled: isPending }}
      closable={!isPending}
      maskClosable={false}
      keyboard={false}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Recipients"
          name="recipients"
          rules={[
            { required: true, message: "Add at least one recipient" },
            {
              validator: (_, value: string[] = []) => {
                if (value.some((entry) => !isValidEmail(entry))) {
                  return Promise.reject(new Error(INVALID_EMAIL_MESSAGE));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select
            mode="tags"
            tokenSeparators={[",", " "]}
            open={false}
            placeholder="Enter recipient emails and press enter"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
